import {
  AnchorButton,
  Colors,
  Icon,
  Intent,
  NonIdealState,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort, isEqual, numericSort } from 'vet-bones/utils';

import { Link } from 'src/components/Link';
import { getProjectDiagramModelTableCellLinkTestId } from 'src/constants/testIds';
import { modelFieldsSelector } from 'src/containers/ProjectDiagram/components/modelList/modelFieldsSelector';
import { ProjectDiagramModelTable } from 'src/containers/ProjectDiagram/components/modelList/ProjectDiagramModelTable';
import {
  ClassTableItem,
  TableField,
} from 'src/containers/ProjectDiagram/components/modelList/types';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { isModelConceptClass } from 'src/containers/ProjectDiagram/utils/data';
import { getAllInheritedAttributes } from 'src/containers/ProjectDiagram/utils/getAllInheritedAttributes';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { projectAttributesByDomainSelector } from 'src/selectors/projectAttributesByDomain';
import { projectClassHierarchySelector } from 'src/selectors/projectClassHierarchy';
import { projectHiddenModelsSelector } from 'src/selectors/projectHiddenModels';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectModelIdsByIriSelector } from 'src/selectors/projectModelsIdsByIri';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { ModelListTab, ModelListTableData } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const INITIAL_FIELDS: Array<TableField<ClassTableItem>> = [
  {
    id: 'name',
    label: copy.components.projectDiagram.modelList.classes.name,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<ClassTableItem>(
        (item) => item.name.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'name',
  },
  {
    cellClassName: 'sd-project-diagram-model-table-cell--monospace',
    hidden: true,
    id: 'iri',
    label: copy.components.projectDiagram.modelList.classes.iri,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<ClassTableItem>((item) => item.iri.value, direction === 'desc'),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'iri',
  },
  {
    cellClassName: 'sd-project-diagram-model-table-cell--center',
    headerClassName: 'sd-project-diagram-model-table-cell--center',
    id: 'attributes',
    label: copy.components.projectDiagram.modelList.classes.attributes,
    sorter: (direction: 'asc' | 'desc') =>
      numericSort<ClassTableItem>(
        (item) => item.attributes.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_NUMERICAL,
    sortIconDesc: IconNames.SORT_NUMERICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortNumberAsc,
    sortLabelDesc:
      copy.components.projectDiagram.modelList.table.sortNumberDesc,
    value: 'attributes',
  },
];

const classTableItemsSelector = (projectId: string) =>
  createSelector(
    entityDrawerSelector,
    projectLabelsByIriSelector(projectId),
    projectNodeDataArraySelector(projectId),
    projectClassHierarchySelector(projectId),
    projectAttributesByDomainSelector(projectId),
    projectModelIdsByIriSelector(projectId),
    projectHiddenModelsSelector(projectId),

    modelFieldsSelector<ClassTableItem>(projectId),
    (
      entityDrawer,
      labelsByIri,
      nodeDataArray,
      hierarchyByIri,
      attributesByDomain,
      modelIdsByIri,
      hiddenModelIds,
      modelFields
    ) =>
      nodeDataArray
        .filter((data) => isModelConceptClass(data))
        .map(
          ({ value: classIri }) =>
            ({
              hidden: Boolean(
                modelIdsByIri[classIri]?.every((model) =>
                  hiddenModelIds?.includes(model)
                )
              ),
              id: classIri,
              name: {
                label: null,
                selected: entityDrawer.entityValue === classIri,
                value: getLabelForIri(
                  labelsByIri,
                  classIri,
                  copy.components.projectDiagram.modelConceptClass.new
                ),
              },
              iri: {
                label: null,
                value: classIri,
              },
              attributes: {
                label: null,
                value: Object.keys(
                  getAllInheritedAttributes({
                    classIri,
                    hierarchyByIri,
                    attributesByDomain,
                  })
                ).length,
              },
              ...Object.fromEntries(
                modelFields.map((field) => [
                  field.value,
                  {
                    label: null,
                    value: {
                      checked: modelIdsByIri[classIri]?.includes(field.value),
                      hidden:
                        !field.value || hiddenModelIds?.includes(field.value),
                    },
                  },
                ])
              ),
            } as ClassTableItem)
        )
        .sort(alphaSort((item) => item.name.value))
  );

export const ProjectDiagramModelClasses: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const modelFields = useReduxSelector(
    modelFieldsSelector<ClassTableItem>(projectId),
    isEqual
  );
  const classItems = useReduxSelector(
    classTableItemsSelector(projectId),
    isEqual
  );

  const handleClickClass = useClickModelConceptClassLink();

  const tableData = useReduxSelector(
    (state) => state.ui.modelList.tableData[ModelListTab.CLASSES]
  );
  const handleUpdateTableData = (data: ModelListTableData<ClassTableItem>) => {
    dispatch(
      uiSlice.actions.updatedModelListTableData({
        tab: ModelListTab.CLASSES,
        data,
      })
    );
  };

  const classFields = React.useMemo(
    () =>
      INITIAL_FIELDS.concat(
        modelFields.map((field) => ({
          ...field,
          sorter: (direction: 'asc' | 'desc') =>
            numericSort<ClassTableItem>(
              (item) => (item[field.value]?.value.checked ? 1 : -1),
              direction === 'desc'
            ),
        }))
      ),
    [modelFields]
  );

  const classItemsWithLinks = React.useMemo(
    () =>
      classItems.map(
        ({ hidden, id, name, iri, attributes, ...models }, idx) =>
          ({
            hidden,
            id,
            name: {
              label: (
                <>
                  <Link
                    className={classNames(
                      'sd-project-diagram-model-table-link',
                      { 'sd-project-diagram-model-table-link--weak': hidden }
                    )}
                    onClick={() => {
                      handleClickClass({
                        text: name.value,
                        value: id,
                      });
                    }}
                    testId={getProjectDiagramModelTableCellLinkTestId(id, idx)}
                  >
                    {name.value}
                  </Link>
                </>
              ),
              selected: name.selected,
              value: name.value,
            },
            iri: {
              label: iri.value,
              value: iri.value,
            },
            attributes: {
              label: attributes.value,
              selected: attributes.selected,
              value: attributes.value,
            },
            ...Object.fromEntries(
              Object.entries(models).map(([modelId, model]) => [
                modelId,
                {
                  label: (
                    <Icon
                      color={
                        !model.value.checked || model.value.hidden
                          ? Colors.GRAY4
                          : undefined
                      }
                      icon={
                        model.value.checked
                          ? IconNames.FULL_CIRCLE
                          : IconNames.CIRCLE
                      }
                    />
                  ),
                  value: model.value,
                },
              ])
            ),
          } as ClassTableItem)
      ),
    [classItems, handleClickClass]
  );

  if (!classItemsWithLinks.length) {
    return (
      <NonIdealState
        action={
          <AnchorButton
            intent={Intent.PRIMARY}
            onClick={() =>
              dispatch(
                uiSlice.actions.toggledCreateClassDrawer({
                  isOpen: true,
                })
              )
            }
            text={
              copy.components.projectDiagram.modelList.classes.emptyState.button
            }
          />
        }
        description={
          copy.components.projectDiagram.modelList.classes.emptyState
            .description
        }
        title={
          copy.components.projectDiagram.modelList.classes.emptyState.title
        }
      />
    );
  }

  return (
    <ProjectDiagramModelTable<ClassTableItem>
      fields={classFields}
      items={classItemsWithLinks}
      onUpdateTableData={handleUpdateTableData}
      tableData={tableData}
    />
  );
};
